/**
 * useFormHandler — Handles form submission and form event coordination
 * Extracted VERBATIM from SimpleCopilotPanel.tsx L475-602
 * 
 * Pre-existing bugs (copied verbatim — DO NOT FIX during refactor):
 * - BUG #3 (R9): Duplicate form-submitted handlers (L287 files + L597 submission) — different purposes
 * - BUG #4 (R19): Inline bot message in handleFormSubmit (L545-561) — doesn't use createBotMessage helper
 * - BUG #5 (R21): handleFormSubmit stale closure — currentActiveFormId + currentFormMetadata missing from useCallback deps
 * 
 * Risks handled:
 * - R3: globalUploadedFiles accessed via ref (7 writers pattern)
 * - R12: form-submitted event listener distributed here (domain-appropriate)
 * - R14: currentActiveFormId mutex — 8 writers, all receive setter from orchestrator
 * - R18: normalizeFormFields imported from shared formUtils
 * 
 * SAFE REFACTORING: No logic changes. Code copied exactly as-is.
 * All state received as params from orchestrator. No state ownership.
 */

import * as React from 'react';
import { postExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';
import { COPILOT_CONSTANTS } from '../constants';
import type { Message } from '../interfaces';
import type { CopilotChatParams } from './CopilotChatParams';
import { normalizeFormFields } from '../utils/formUtils';
import { formInstance, normalizeBlobReference, reconcileFormValues, serializeFormValues } from '../utils/genericFormTransport';
import { validateAskResponse, validateQueryLength } from '../utils/queryLimit';
import { formatAndConvertByType } from '../utils/formatAndConvertByType';
import { extractTicketNumber } from '../utils/ticketExtraction';
import { processFilesOnly } from '../../../services/fileUploadService';
import type { SelfHelpFeedbackFormAssociation } from '../interfaces/api';
import {
  clearPendingSelfHelpFeedbackSubmission,
  completeSelfHelpFeedbackJourney,
  emitSelfHelpFeedbackJourneyEvent,
  getPendingSelfHelpFeedbackSubmission,
} from '../utils/selfHelpFeedbackSession';
import {
  isConfirmedTicketOutcome,
  readTicketOutcome,
} from '../utils/ticketOutcome';

/**
 * Hook for handling form submissions and form-related events
 * Returns handleFormSubmit callback
 */
export function useFormHandler(params: CopilotChatParams) {
  const {
    setMessages,
    setLoading,
    currentFormMetadata,
    setCurrentFormMetadata,
    setCurrentFormQuery,
    currentActiveFormId,
    setCurrentActiveFormId,
    setSubmittedFormIds,
    setPersistentFormValues,
    setCurrentTicketNumber,
    sessionIdRef,
    scrollRef,
    lastAnchoredMessageIdRef,
    globalUploadedFilesRef,
    effectiveApi,
    userEmail,
    mcapsTelemetry,
    platformAuthClient,
  } = params;
  const renderedConversationId = sessionIdRef.current;
  const submittingRef = React.useRef<{ conversation: string } | null>(null);
  const activeFormRef = React.useRef(currentActiveFormId);
  activeFormRef.current = currentActiveFormId;
  const uploadedReferencesRef = React.useRef(new Map<File, { session: string; field: string; reference: string }>());

  // Verbatim from SimpleCopilotPanel.tsx L475-477
  const cleanupSessionData = () => {
    globalUploadedFilesRef.current = [];
  };

  const selfHelpFeedbackSubmission =
    (currentFormMetadata?.selfHelpFeedbackSubmission as
      | SelfHelpFeedbackFormAssociation
      | undefined) ??
    getPendingSelfHelpFeedbackSubmission(sessionIdRef.current);

  const emitSubmissionOutcome = (
    type: 'ticket-submission-succeeded' | 'ticket-submission-failed'
  ) => {
    if (selfHelpFeedbackSubmission) {
      emitSelfHelpFeedbackJourneyEvent({
        type,
        ...selfHelpFeedbackSubmission,
      });
    }
  };

  // Story 1236552: Upload file attachment during form fill or post-creation
  const uploadFormAttachment = async (file: File, uploadParams: { sessionId?: string; fieldId?: string; ticketId?: string }) => {
    const attachmentUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FORM_ATTACHMENT;
    const formPayload = new FormData();
    formPayload.append('file', file, file.name);
    if (uploadParams.sessionId) formPayload.append('sessionId', uploadParams.sessionId);
    if (uploadParams.fieldId) formPayload.append('fieldId', uploadParams.fieldId);
    if (uploadParams.ticketId) formPayload.append('ticketId', uploadParams.ticketId);
    const appName = sessionStorage.getItem('mcapsCopilot_appName');
    if (appName) formPayload.append('appName', appName);
    try {
      const response = await postExternalConsumptionAPI(attachmentUrl, formPayload, platformAuthClient);
      return response?.data;
    } catch (error) {
      console.error('[Attachment] Upload failed:', error);
      return { success: false, validationError: 'Upload failed. Please try again.' };
    }
  };

  // Verbatim from SimpleCopilotPanel.tsx L479-583
  // FIX: Added currentActiveFormId + currentFormMetadata to deps (was PRE-EXISTING stale closure)
  const handleFormSubmit = React.useCallback(async (formData: {
    values: Record<string, any>;
    isValid: boolean;
    errors: Record<string, string>;
    uploadedFiles?: File[];
    payload?: any;
    cancelForm?: boolean;
    getNewerValues?: () => Record<string, any>;
  }) => {
    if (sessionIdRef.current !== renderedConversationId ||
        submittingRef.current?.conversation === sessionIdRef.current ||
        !currentActiveFormId || activeFormRef.current !== currentActiveFormId) return;
    const requestConversationId = sessionIdRef.current;
    const requestFormId = currentActiveFormId;
    const isCurrent = () => sessionIdRef.current === requestConversationId && activeFormRef.current === requestFormId;
    const uploadScope = `${requestConversationId}:${requestFormId}`;
    formData = { ...formData, values: { ...formData.values, ...formData.payload },
      ...(formData.payload && { payload: { ...formData.payload } }) };
    if (!formData.isValid && !formData.cancelForm) {
      return;
    }
    const submission = { conversation: requestConversationId };
    submittingRef.current = submission;
    setLoading(true);
    try {

    // Story 1236552: Upload files to blob via /attachment BEFORE submitting form values
    let filesToUpload = formData.cancelForm ? [] : formData.uploadedFiles || globalUploadedFilesRef.current || [];
    
    // Track files that were successfully uploaded to blob storage (to avoid duplicate upload later)
    const filesUploadedToBlob: File[] = [];
    
    // Story 1244484: Extract File objects from formData.values if uploadedFiles is empty
    // The copilot web component puts File objects directly in values, not in uploadedFiles
    const fileFieldsInValues: { fieldId: string; files: File[] }[] = [];
    if (!formData.cancelForm && formData.values) {
      for (const [key, value] of Object.entries(formData.values)) {
        if (value instanceof File) {
          // Single File object
          filesToUpload = [...filesToUpload.filter(f => f !== value), value];
          fileFieldsInValues.push({ fieldId: key, files: [value] });
        } else if (value instanceof Blob && !(value instanceof File)) {
          // Blob object (convert to File for upload)
          const blobFile = new File([value], 'attachment', { type: value.type });
          filesToUpload = [...filesToUpload, blobFile];
          fileFieldsInValues.push({ fieldId: key, files: [blobFile] });
        } else if (Array.isArray(value) && value.some(item => item instanceof File)) {
          // Array of File objects
          const files = value.filter(item => item instanceof File);
          filesToUpload = [...filesToUpload.filter(f => !files.includes(f)), ...files];
          fileFieldsInValues.push({ fieldId: key, files });
        } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof Blob) {
          // Array of Blobs - convert to Files
          const blobFiles = value.map((b, i) => new File([b], `attachment_${i}`, { type: b.type }));
          filesToUpload = [...filesToUpload, ...blobFiles];
          fileFieldsInValues.push({ fieldId: key, files: blobFiles });
        }
        filesToUpload = Array.from(new Set(filesToUpload));
        // Removed duck-typing check to avoid false positives with regular objects
      }
    }
    
    if (filesToUpload.length > 0 && sessionIdRef.current) {
      // Story 1236552 fix (UAT null-payload): the file field id used to tag the blob upload MUST
      // come from the same authoritative source used to build the submit CSV below
      // (currentFormMetadata.formFields). The event-derived formData.fields is frequently empty on
      // file-fill submits, which made this lookup fall through to the 'uploadTemplate' fallback.
      // That tagged the uploaded blob with the wrong fieldId, so its blob reference was stored under
      // 'uploadTemplate' instead of the real file field (e.g. 'attachments'). The CSV builder then
      // emitted an empty value for the required file field, leaving the backend payload null and
      // silently blocking ticket creation.
      const eventFormFields = (formData as any).formData?.fields || formData.payload?.fields || formData.values?.fields;
      const metadataFormFields = currentFormMetadata?.formFields ? normalizeFormFields(currentFormMetadata) : undefined;
      const formFields =
        (Array.isArray(metadataFormFields) && metadataFormFields.length > 0)
          ? metadataFormFields
          : (Array.isArray(eventFormFields) && eventFormFields.length > 0 ? eventFormFields : []);
      const defaultFileFieldId = (() => {
        for (const field of (Array.isArray(formFields) ? formFields : [])) {
          if (field?.type === 'file') return field.id;
        }
        return 'uploadTemplate';
      })();

      let uploadedCount = 0;
      let failedCount = 0;
      const failedNames: string[] = [];
      
      // Story 1244484: Track which field IDs have been updated with blob references
      const uploadedFieldRefs: Map<string, string> = new Map();
      const metadataValues = currentFormMetadata?.formJson?.collectedData || {};
      const tokens = (value: any): string[] => (Array.isArray(value) ? value : [value])
        .filter(item => typeof item === 'string').flatMap(item => item.split(','))
        .filter(item => item.startsWith('blob:'));
      const fieldForFile = (file: File) => fileFieldsInValues.find(entry => entry.files.includes(file))?.fieldId || defaultFileFieldId;
      let total = 0;
      for (const field of formFields.filter((item: any) => item.type === 'file')) {
        const maximum = Math.min(Math.max(field.validation?.maxCount ?? 1, 1), 5);
        const selected = filesToUpload.filter(file => fieldForFile(file) === field.id);
        const existing = Array.from(new Set([
          ...tokens(metadataValues[field.id]), ...tokens(formData.values?.[field.id]),
          ...Array.from(uploadedReferencesRef.current.values())
            .filter(entry => entry.session === uploadScope && entry.field === field.id)
            .map(entry => entry.reference),
        ]));
        const pending = selected.filter(file => {
          const cached = uploadedReferencesRef.current.get(file);
          return cached?.session !== uploadScope || !existing.includes(cached.reference);
        });
        const count = maximum > 1 ? existing.length + pending.length : selected.length || existing.length;
        total += count;
        if (count > maximum || selected.some(file => file.size > (field.validation?.maxSizeMB ?? 25) * 1024 * 1024)) {
          throw new Error(`Check the attachment limit for this field: up to ${maximum} file(s), ${field.validation?.maxSizeMB ?? 25} MB each. Your draft has been retained.`);
        }
        if (maximum > 1 && existing.length) uploadedFieldRefs.set(field.id, existing.join(','));
      }
      if (total > 5) throw new Error('A form supports up to 5 attachments. Your draft has been retained.');

      for (const file of filesToUpload) {
        // Determine the field ID for this file
        let fileFieldId = defaultFileFieldId;
        for (const extracted of fileFieldsInValues) {
          if (extracted.files.includes(file)) {
            fileFieldId = extracted.fieldId;
            break;
          }
        }
        
        try {
          const cached = uploadedReferencesRef.current.get(file);
          const result = cached?.session === uploadScope && cached.field === fileFieldId
            ? { success: true, blobReference: cached.reference }
            : await uploadFormAttachment(file, {
            sessionId: requestConversationId,
            fieldId: fileFieldId
          });
          if (!isCurrent()) return;
          if (result?.success && result.blobReference) {
            uploadedCount++;
            filesUploadedToBlob.push(file); // Track successful blob uploads
            // Story 1236552: Store blob formInstanceId as field value (not file object)
            if (result.blobReference) {
              const reference = normalizeBlobReference(result.blobReference);
              if ((formFields.find((field: any) => field.id === fileFieldId)?.validation?.maxCount ?? 1) <= 1) {
                uploadedReferencesRef.current.forEach((entry, uploadedFile) => {
                  if (entry.session === uploadScope && entry.field === fileFieldId && uploadedFile !== file) {
                    uploadedReferencesRef.current.delete(uploadedFile);
                  }
                });
              }
              uploadedReferencesRef.current.set(file, { session: uploadScope, field: fileFieldId, reference });
              const previous = uploadedFieldRefs.get(fileFieldId);
              uploadedFieldRefs.set(fileFieldId, Array.from(new Set([...(previous?.split(',') || []), reference])).join(','));
            } else {
              failedCount++;
            }
          } else {
            failedCount++;
            failedNames.push(result?.validationError || 'Upload failed. Please try again.');
          }
        } catch (err) {
          if (!isCurrent()) return;
          failedCount++;
          failedNames.push('Upload failed. Please try again.');
        }
      }
      
      // Story 1244484: Update form values with blob references for all uploaded fields
      for (const [fieldId, blobRef] of uploadedFieldRefs.entries()) {
        if (failedCount > 0) continue;
        formData.values = formData.values || {};
        formData.values[fieldId] = blobRef;
        if (formData.payload) {
          formData.payload[fieldId] = blobRef;
        }
        setPersistentFormValues({ ...formData.values });
        globalUploadedFilesRef.current = filesToUpload.filter(file => !filesUploadedToBlob.includes(file));
      }

      if (failedCount > 0) {
        setPersistentFormValues({ ...formData.values });
        setMessages((prev: any) => [...prev, {
          id: `error-upload-${Date.now()}`,
          role: 'assistant' as const,
          content: `⚠️ ${failedCount} file(s) failed to upload. ${failedNames.join(' ')} Your draft has been retained.`,
          timestamp: Date.now()
        }]);
        return;
      }
    }

      setLoading(true);
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
      const formValues = formData.payload || formData.values || {};
      mcapsTelemetry.logComponentInteraction({
        action: 'submit',
        component: 'SimpleCopilotPanel'
      });
      // Escape double quotes inside field values to prevent CSV-format breakage.
      // Robust against non-string field values (objects, arrays) so we never emit the
      // useless "[object Object]" / "{}" that breaks positional CSV parsing on the backend.
      const extractScalar = (v: any): string => {
        if (v === null || v === undefined) return '';
        if (Array.isArray(v)) {
          // Preserve the pre-existing String(array) behavior (comma-joined) for primitive
          // arrays so previously-working values are byte-for-byte identical; only arrays of
          // objects (which used to become "[object Object],[object Object]") now improve.
          return v.map(extractScalar).filter(s => s !== '').join(',');
        }
        if (typeof v === 'object') {
          // Lookup / people-picker / composite field: pull a meaningful scalar.
          const s =
            v.value ?? v.text ?? v.display ?? v.displayName ??
            v.name ?? v.label ?? v.id ?? v.key;
          // No recognizable scalar → treat as empty (matches an unselected optional field),
          // NOT "[object Object]" or "{}" which the backend rejects.
          return s !== undefined && s !== null ? String(s) : '';
        }
        return String(v);
      };
      const escapeFieldValue = (val: any): string => {
        const str = extractScalar(val);
        return `"${str.replace(/"/g, '""')}"`;
      };

      let currentFormValuesArray;
      // File-type fields normally carry a serialized blob reference string ("blob:<id>")
      // AFTER a successful attachment upload — that MUST be preserved so the backend can
      // link the attachment. But if the upload didn't complete, the value is still a raw
      // File/object which String()-ifies to "[object Object]" and breaks positional CSV
      // parsing. So for file fields: keep string values, drop non-string (File) values.
      const resolveFieldValue = (field: any): string => {
        const byName = formValues[field.name];
        const raw =
          byName === '' || byName === null || byName === undefined
            ? (formValues[field.id] ?? '')
            : byName;
        if (field?.type === 'file') {
          return escapeFieldValue(typeof raw === 'string' ? raw : '');
        }
        return escapeFieldValue(raw);
      };
      if (currentFormMetadata && currentFormMetadata.formFields) {
        const orderedFields = normalizeFormFields(currentFormMetadata);
        currentFormValuesArray = orderedFields
          .filter((field: any) => field.type !== 'info') // Skip info fields from query
          .map((field: any) => resolveFieldValue(field));
      } else {
        // Fallback: use fields from event's formData or payload to filter info fields
        const payloadFields = (formData as any).formData?.fields || formData.payload?.fields || formData.values?.fields;
        if (Array.isArray(payloadFields) && payloadFields.length > 0) {
          currentFormValuesArray = payloadFields
            .filter((field: any) => field.type !== 'info')
            .map((field: any) => resolveFieldValue(field));
        } else {
          currentFormValuesArray = Object.values(formValues).map(value => escapeFieldValue(value));
        }
      }
      const nativeFields = currentFormMetadata?.formFields
        ? normalizeFormFields(currentFormMetadata)
        : (formData as any).formData?.fields || formData.payload?.fields;
      let commaSeparatedValues = Array.isArray(nativeFields)
        ? serializeFormValues(nativeFields, formValues)
        : currentFormValuesArray.join(',');
      if (formData.cancelForm && commaSeparatedValues.length > COPILOT_CONSTANTS.INPUT_LIMITS.MAX_CHARACTER_COUNT) {
        commaSeparatedValues = '';
      }
      validateQueryLength(commaSeparatedValues);
      // Include appName context for ECIF integration
      const appName = sessionStorage.getItem('mcapsCopilot_appName');
      const apiPayload = {
        query: commaSeparatedValues,
        sessionId: requestConversationId,
        userEmail: userEmail,
        ...(formData.cancelForm && { cancelForm: true }),
        ...(appName && { appName: appName })
      };

      //const queryStartTime = Date.now();
      const response = await postExternalConsumptionAPI(
        effectiveApi,
        apiPayload,
        platformAuthClient
      );
      if (!isCurrent()) return;
      validateAskResponse(response);
      const data = response?.data || response;
      const answerText: string = data.response || data.result || '';
      // const queryEndTime = Date.now();
      // const processingTime = queryEndTime - queryStartTime;
      const ticketOutcome = readTicketOutcome(data);
      const confirmedTicketOutcome =
        isConfirmedTicketOutcome(ticketOutcome);
      const extractedTicketNumber = ticketOutcome
        ? confirmedTicketOutcome
          ? ticketOutcome.ticketNumber
          : null
        : extractTicketNumber(answerText, data);
      if (extractedTicketNumber) {
        setCurrentTicketNumber(extractedTicketNumber);
        sessionStorage.setItem('mcapsCopilot_ticketNumber', extractedTicketNumber);
        
        // Story 1244484: Only attach files that were NOT already uploaded to blob storage.
        // Files uploaded via the blob path (uploadFormAttachment) are linked to the ticket by the
        // backend at creation time, so processFilesOnly() (UploadTicketAttachments) must NOT re-attach
        // them or the ticket ends up with two copies (one server-timestamped blob entry + one
        // original-name entry). Dedup by a stable content key (name|size|lastModified) rather than
        // File-object reference identity: the copilot web component re-materializes File objects, so
        // the old Array.includes() reference check let already-uploaded files slip through and get
        // attached twice. Files that genuinely failed blob upload are absent from filesUploadedToBlob
        // and so are still rescued by processFilesOnly below (fallback preserved).
        const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
        const uploadedToBlobKeys = new Set(filesUploadedToBlob.map(fileKey));
        const filesNotUploadedToBlob = filesToUpload.filter(f => !uploadedToBlobKeys.has(fileKey(f)));
        if (filesNotUploadedToBlob.length > 0) {
          // Skip separate attachment upload for HRD365 — backend handles attachments
          // during ticket creation via createwithattachment endpoint
          const isHrd365Ticket = /^(CAS|HR)-/i.test(extractedTicketNumber);
          if (!isHrd365Ticket) {
            try {
              await processFilesOnly(filesNotUploadedToBlob, extractedTicketNumber, platformAuthClient);
              if (!isCurrent()) return;
            } catch (fileErr) {
              if (!isCurrent()) return;
              console.error(`[Attachment] Failed to attach files to ticket:`, fileErr);
            }
          }
        }
        emitSubmissionOutcome('ticket-submission-succeeded');
        if (selfHelpFeedbackSubmission) {
          setMessages(previousMessages =>
            completeSelfHelpFeedbackJourney(
              previousMessages,
              selfHelpFeedbackSubmission
            )
          );
          clearPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
        }
      }

      if (
        confirmedTicketOutcome ||
        (!ticketOutcome &&
          Boolean(extractedTicketNumber) &&
          Boolean(selfHelpFeedbackSubmission))
      ) {
        if (confirmedTicketOutcome && currentActiveFormId) {
          setSubmittedFormIds(prev => new Set([...prev, currentActiveFormId]));
        }
        setCurrentFormMetadata(null);
        setCurrentFormQuery('');
        setCurrentActiveFormId(null);
        setPersistentFormValues({});
      } else if (data.formSessionActive && !extractedTicketNumber) {
        // Story 1236552: Clear stale ticketId when a new form session starts
        sessionStorage.removeItem('mcapsCopilot_ticketNumber');
        setCurrentTicketNumber(null);
      }

      mcapsTelemetry.logResponseReceived({
        query: `Form submission: fieldCount=${currentFormValuesArray.length}`,
        response: answerText || 'Form submitted (no response)'
      });
      const formatted = formatAndConvertByType(data);
      const nextFields = formatted.format === 'form'
        ? normalizeFormFields(formatted.dataToDisplay) : undefined;
      const sameForm = Array.isArray(nextFields) && Array.isArray(nativeFields) &&
        formInstance(nextFields) === formInstance(nativeFields) &&
        ['formKey', 'subsectionId', 'currentSubsectionKey'].every(key =>
          currentFormMetadata?.formJson?.[key] === formatted.dataToDisplay.formJson?.[key]) &&
        (['authoritative', 'preserveDraft'].includes(formatted.dataToDisplay.formJson?.collectedDataMode) ||
          Boolean(formatted.dataToDisplay.formJson?.fieldErrors &&
          Object.keys(formatted.dataToDisplay.formJson.fieldErrors).length) ||
          Boolean(formatted.dataToDisplay.formJson?.globalErrors?.length) ||
          JSON.stringify(nextFields.map((field: any) => field.id)) ===
          JSON.stringify(nativeFields.map((field: any) => field.id)));
      if (sameForm && currentActiveFormId) {
        const next = formatted.dataToDisplay;
        const collectedData = reconcileFormValues(
          nextFields!, next.formJson, formValues, formData.getNewerValues?.()
        );
        const updated = {
          ...next,
          formJson: {
            ...next.formJson,
            collectedData,
          },
        };
        setMessages(prev => prev.map(message => message.id === currentActiveFormId ? {
          ...message,
          meta: { ...message.meta, dataToDisplay: updated, formFields: nextFields },
        } : message));
        setCurrentFormMetadata(updated);
        setPersistentFormValues(collectedData);
        cleanupSessionData();
        return;
      }
      if (formatted.format !== 'form' && data.formSessionActive === false) {
        setMessages(prev => prev.map(message => message.id === currentActiveFormId ? {
          ...message,
          meta: {
            ...message.meta,
            dataToDisplay: {
              ...message.meta?.dataToDisplay,
              formJson: {
                ...message.meta?.dataToDisplay?.formJson,
                collectedData: { ...message.meta?.dataToDisplay?.formJson?.collectedData, ...formValues },
              },
            },
          },
        } : message));
        setSubmittedFormIds(prev => new Set([...prev, currentActiveFormId!]));
        setCurrentActiveFormId(null);
        setCurrentFormMetadata(null);
        setCurrentFormQuery('');
        setPersistentFormValues({});
        activeFormRef.current = null;
        uploadedReferencesRef.current.clear();
      }
      if (
        currentActiveFormId &&
        (Boolean(extractedTicketNumber) || formatted.format === 'form')
      ) {
        setSubmittedFormIds(prev => new Set([...prev, currentActiveFormId]));
      }
      if (
        selfHelpFeedbackSubmission &&
        !extractedTicketNumber &&
        formatted.format !== 'form'
      ) {
        emitSubmissionOutcome('ticket-submission-failed');
      }
      // BUG #4 (R19): Inline bot message construction — doesn't use createBotMessage helper. PRE-EXISTING, copy verbatim.
      if (answerText.trim() || formatted.format === 'form') {
        const botMessage: Message = {
          id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ASSISTANT + Date.now(),
          role: 'assistant',
          content: answerText || 'Form',
          timestamp: Date.now(),
          meta: {
            processingTime: data.processingTime,
            confidence: data.confidence,
            ticketGuidance: data.ticketGuidance || data.ticketGuidence || undefined,
            responseType: formatted.format?.toLowerCase().includes("chart") ? "graph" : formatted.format,
            dataToDisplay: formatted.dataToDisplay,
            formFields: formatted.format === 'form' ? normalizeFormFields(formatted.dataToDisplay) : undefined,
            formTitle: formatted.format === 'form' ? formatted.dataToDisplay.formTitle : undefined,
            formDescription: formatted.format === 'form' ? formatted.dataToDisplay.formDescription : undefined,
            formSessionActive: data.formSessionActive || false
          }
        };
        // WI 1279351: Keep the user anchored on the submission outcome. The [messages] scroll effect
        // anchors the message referenced by lastAnchoredMessageIdRef to the top of the viewport.
        // useMessageSender/useAdaptiveCardHandler set it to the user's question, but the form-submit
        // path never updated it — so on a terminal (non-form) response like the "Ticket Created"
        // confirmation, the anchor pointed at a STALE earlier question and the view jumped up, pushing
        // the Action ID / Status / Link out of view. Anchor the confirmation itself so it stays at the
        // top (its ID/Status/Link are at the top of the message → remain visible). Intermediate form
        // steps (format === 'form') keep the existing behavior.
        if (formatted.format !== 'form') {
          lastAnchoredMessageIdRef.current = botMessage.id;
        }
        setMessages(prev => [...prev, botMessage]);
        if (formatted.format === 'form') {
          if (Array.isArray(nextFields) && formInstance(nextFields) !== formInstance(nativeFields || [])) {
            setPersistentFormValues(reconcileFormValues(nextFields, formatted.dataToDisplay.formJson, {}));
            uploadedReferencesRef.current.clear();
          }
          setCurrentFormMetadata({
            ...formatted.dataToDisplay,
            ...(selfHelpFeedbackSubmission ? { selfHelpFeedbackSubmission } : {}),
          });
          setCurrentActiveFormId(botMessage.id);
          activeFormRef.current = botMessage.id;
        }
      }
      cleanupSessionData();
    } catch (error: any) {
      if (!isCurrent()) return;
      setPersistentFormValues({ ...formData.values });
      emitSubmissionOutcome('ticket-submission-failed');
      mcapsTelemetry.logError({
        query: 'Form submission',
        error: error.message
      });
      const errorMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
        role: 'error',
        content: `Form submission failed: ${error.message}`,
        timestamp: Date.now()
      };
      // WI 1279351: anchor the error too, so a failed submit stays on the error message
      // instead of jumping up to a stale earlier question.
      lastAnchoredMessageIdRef.current = errorMessage.id;
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (submittingRef.current === submission) {
        submittingRef.current = null;
        if (sessionIdRef.current === requestConversationId) setLoading(false);
      }
    }
  }, [effectiveApi, userEmail, mcapsTelemetry, currentActiveFormId, currentFormMetadata, setCurrentFormMetadata, renderedConversationId]);

  // Verbatim from SimpleCopilotPanel.tsx L586-602
  // BUG #3 (R9): This is the SECOND form-submitted handler (first is at L282 for files). PRE-EXISTING pattern.
  React.useEffect(() => {
    const handleFormSubmittedEvent = (event: any) => {
      const { payload, formData, uploadedFiles } = event.detail || {};

      const formSubmissionData = {
        values: payload || {},
        isValid: true,
        errors: {},
        uploadedFiles: uploadedFiles || globalUploadedFilesRef.current || [],
        formData: formData
      };
      handleFormSubmit(formSubmissionData);
    };
    document.addEventListener('form-submitted', handleFormSubmittedEvent as EventListener);
    return () => {
      document.removeEventListener('form-submitted', handleFormSubmittedEvent as EventListener);
    };
  }, [handleFormSubmit]);

  return { handleFormSubmit };
}

// Generated by Copilot
// updated on: 2026-09-09 | Story 1297509 UI audit | Emitted ticket completion only from shared submission success or failure | Time Taken 6 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Kept retryable submission failures active until form progression or ticket success | Time Taken 4 min
// updated on: 2026-09-14 | Story 1297509 review | Removed submitted form values from client telemetry | Time Taken 1 min
// updated on: 2026-09-18 | 8 lines changed | [Auto Generated] | Complete GPS feedback telemetry from pending form association | Time Taken 3 min
// Generated by Copilot
// updated on: 2026-10-16 | 7 lines added/updated | [Auto Generated] | Refresh submission field metadata after each form transition and retain feedback attribution | Time Taken 5 min
// updated on: 2026-10-16 | [Auto Generated] | Terminalized confirmed forms from structured ticket outcomes | Time Taken 3 min
// updated on: 2026-10-18 | 13 lines changed | [Auto Generated] | Resolve completed self-help journey after form ticket creation | Time Taken 3 min
// Generated by Copilot
// updated on: 2026-10-26 | 80 lines added/updated | [Auto Generated] | Submit native generic CSV with blob refs and preserve drafts validation and terminal history | Time Taken 12 min
// Generated by Copilot
// updated on: 2026-10-26 | 9 lines added / 4 removed | [Auto Generated] | Honor authoritative and preserveDraft native response contracts | Time Taken 10 min
// Generated by Copilot
// updated on: 2026-10-26 | 94 lines added/updated/removed | [Auto Generated] | Bound complete CSV and uploads preserve retries and retire form instances | Time Taken 8 min
